import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import {
  ArrowCounterClockwise,
  CaretDown,
  CaretRight,
  CheckCircle,
  DownloadSimple,
  FloppyDisk,
  Trash,
  WarningCircle,
  X
} from '@phosphor-icons/react';

// Default empty sections for "Custom" preset
const EMPTY_SECTIONS = {
  overview: {
    cover: false,
    exec_summary: false,
    readiness: false,
    org_snapshot: false
  },
  financial: {
    headline: false,
    roi_summary: false,
    roi_detailed: false,
    assumptions: false
  },
  opportunities: {
    summary: false,
    top_n: false,
    top_n_count: 10,
    full_list: false,
    quick_wins: false,
    medium: false,
    long_term: false,
    filter_by_status: false,
    selected_statuses: []
  },
  roadmap: {
    include: false,
    phases: true,
    quick_wins: true,
    timeline: true
  },
  org_chart: {
    include: false,
    overlay: false,
    role_table: false
  },
  agent_configs: {
    include: false,
    top_n: true,
    top_n_count: 5,
    full_list: false
  },
  appendices: {
    methodology: false,
    consultant_notes: false,
    custom_notes_text: ''
  }
};

const ReportBuilderModal = ({ isOpen, onClose, organizationId, onGenerate }) => {
  const [selectedSections, setSelectedSections] = useState({
    overview: {
      cover: true,
      exec_summary: true,
      readiness: true,
      org_snapshot: true
    },
    financial: {
      headline: true,
      roi_summary: true,
      roi_detailed: false,
      assumptions: false
    },
    opportunities: {
      summary: true,
      top_n: true,
      top_n_count: 10,
      full_list: false,
      quick_wins: false,
      medium: false,
      long_term: false,
      filter_by_status: false,
      selected_statuses: []
    },
    roadmap: {
      overview: true,
      detailed: false,
      next_moves: true
    },
    org_chart: {
      overlay: false,
      role_table: false
    },
    agent_configs: {
      include: false,
      selected_only: false,
      selected_opportunity_ids: []
    },
    appendices: {
      methodology: true,
      disclaimer: true,
      consultant_notes: false,
      custom_notes_text: ""
    }
  });

  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('full_detail');
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    overview: true,
    financial: false,
    opportunities: false,
    roadmap: false,
    org_chart: false,
    agent_configs: false,
    appendices: false
  });
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadPresets();
    }
  }, [isOpen]);

  const loadPresets = async () => {
    try {
      const headers = {};
      if (organizationId) {
        headers['X-Organization-ID'] = organizationId;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/report-presets`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets || []);
      }
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const applyPreset = (presetId) => {
    if (presetId === 'custom') {
      // Clear all checkboxes for custom preset
      setSelectedSections(EMPTY_SECTIONS);
      setSelectedPreset('custom');
      return;
    }
    
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      // Default sections structure to merge with preset sections
      const defaultSections = {
        overview: {
          cover: true,
          exec_summary: true,
          readiness: true,
          org_snapshot: true
        },
        financial: {
          headline: true,
          roi_summary: true,
          roi_detailed: false,
          assumptions: false
        },
        opportunities: {
          summary: true,
          top_n: true,
          top_n_count: 10,
          full_list: false,
          quick_wins: false,
          medium: false,
          long_term: false,
          filter_by_status: false,
          selected_statuses: []
        },
        roadmap: {
          overview: true,
          detailed: false,
          next_moves: true
        },
        org_chart: {
          overlay: false,
          role_table: false
        },
        agent_configs: {
          include: false,
          selected_only: false,
          selected_opportunity_ids: []
        },
        appendices: {
          methodology: true,
          disclaimer: true,
          consultant_notes: false,
          custom_notes_text: ""
        }
      };

      // Merge preset sections with defaults (preset values take precedence)
      const mergedSections = {};
      for (const key of Object.keys(defaultSections)) {
        mergedSections[key] = {
          ...defaultSections[key],
          ...(preset.sections?.[key] || {})
        };
      }

      setSelectedSections(mergedSections);
      setSelectedPreset(presetId);
    }
  };

  const handleDeletePreset = async (presetId) => {
    if (!window.confirm('Are you sure you want to delete this preset?')) {
      return;
    }

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (organizationId) {
        headers['X-Organization-ID'] = organizationId;
      }

      const response = await fetch(`${BACKEND_URL}/api/report-presets/${presetId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        await loadPresets();
        // If the deleted preset was selected, switch to first available
        if (selectedPreset === presetId) {
          setSelectedPreset('custom');
          setSelectedSections(EMPTY_SECTIONS);
        }
        alert('Preset deleted successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.detail || 'Failed to delete preset');
      }
    } catch (error) {
      console.error('Failed to delete preset:', error);
      alert('Failed to delete preset');
    }
  };

  const handleSavePreset = async () => {
    if (!newPresetName.trim()) {
      alert('Please enter a preset name');
      return;
    }

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (organizationId) {
        headers['X-Organization-ID'] = organizationId;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/report-presets`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          name: newPresetName,
          description: newPresetDescription,
          sections: selectedSections
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPresets([...presets, data.preset]);
        setShowSavePreset(false);
        setNewPresetName('');
        setNewPresetDescription('');
        setSelectedPreset(data.preset.id);
        alert('Preset saved successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.detail || 'Failed to save preset. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save preset:', error);
      alert('Failed to save preset. Please try again.');
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          organization_id: organizationId,
          report_config: {
            preset_id: selectedPreset,
            sections: selectedSections
          }
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `AI_Opportunity_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        if (onGenerate) onGenerate();
        onClose();
      } else {
        throw new Error('Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleSection = (group, section) => {
    setSelectedSections(prev => ({
      ...prev,
      [group]: {
        ...prev[group],
        [section]: !prev[group][section]
      }
    }));
    setSelectedPreset('custom');
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const updateField = (group, field, value) => {
    setSelectedSections(prev => ({
      ...prev,
      [group]: {
        ...prev[group],
        [field]: value
      }
    }));
  };

  const resetToDefaults = () => {
    applyPreset('full_detail');
  };

  if (!isOpen) return null;

  const atLeastOneSelected = Object.values(selectedSections).some(group =>
    Object.values(group).some(val => val === true)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[#1A1B1E] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[rgba(255,255,255,0.1)] flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Build Report</h2>
            <p className="text-sm text-[#A0AEC0] mt-1">Choose what to include before exporting your AI Opportunity Report</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Preset Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-[#A0AEC0]">Preset:</label>
              <select
                value={selectedPreset}
                onChange={(e) => applyPreset(e.target.value)}
                className="px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              >
                <option value="custom">Custom (Start Fresh)</option>
                {presets.map(preset => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name}{preset.is_builtin ? '' : ' ✦'}
                  </option>
                ))}
              </select>
              
              {/* Trash button for custom (non-builtin) presets */}
              {selectedPreset && selectedPreset !== 'custom' && (() => {
                const currentPreset = presets.find(p => p.id === selectedPreset);
                return currentPreset && !currentPreset.is_builtin;
              })() && (
                <button
                  onClick={() => handleDeletePreset(selectedPreset)}
                  className="p-2 rounded-lg hover:bg-[rgba(239,68,68,0.2)] text-[#EF4444] transition-colors"
                  title="Delete this preset"
                >
                  <Trash size={16} />
                </button>
              )}
            </div>

            <button
              onClick={() => setShowSavePreset(true)}
              className="px-3 py-1.5 rounded-lg border border-[rgba(0,229,192,0.2)] bg-[rgba(0,229,192,0.06)] text-[#00E5C0] hover:bg-[rgba(0,229,192,0.12)] hover:border-[rgba(0,229,192,0.35)] transition-all text-xs font-medium flex items-center gap-1.5"
            >
              <FloppyDisk size={14} />
              Save Preset
            </button>

            <button
              onClick={resetToDefaults}
              className="px-3 py-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] text-[#A0AEC0] hover:text-white transition-colors text-sm"
              title="Reset to defaults"
            >
              <ArrowCounterClockwise size={16} />
            </button>

            <button
              onClick={onClose}
              className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
            >
              <X size={20} className="text-white" />
            </button>
          </div>
        </div>

        {/* Warning */}
        {!atLeastOneSelected && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-start gap-2">
            <WarningCircle size={18} className="text-[#EF4444] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#EF4444]">
              Please select at least one section to include in your report.
            </p>
          </div>
        )}

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {/* Section Group A - Overview */}
            <SectionGroup
              title="Overview"
              isExpanded={expandedGroups.overview}
              onToggle={() => toggleGroup('overview')}
            >
              <Checkbox
                label="Cover Page"
                checked={selectedSections.overview.cover}
                onChange={() => toggleSection('overview', 'cover')}
                description="Client logo, report title, date"
              />
              <Checkbox
                label="Executive Summary"
                checked={selectedSections.overview.exec_summary}
                onChange={() => toggleSection('overview', 'exec_summary')}
              />
              <Checkbox
                label="AI Readiness Summary"
                checked={selectedSections.overview.readiness}
                onChange={() => toggleSection('overview', 'readiness')}
                description="Current grade (A-E) + interpretation"
              />
              <Checkbox
                label="Organization Snapshot"
                checked={selectedSections.overview.org_snapshot}
                onChange={() => toggleSection('overview', 'org_snapshot')}
                description="Industry, size, key context"
              />
            </SectionGroup>

            {/* Section Group B - Financial */}
            <SectionGroup
              title="Financial & ROI"
              isExpanded={expandedGroups.financial}
              onToggle={() => toggleGroup('financial')}
            >
              <Checkbox
                label="Potential Annual Savings (headline)"
                checked={selectedSections.financial.headline}
                onChange={() => toggleSection('financial', 'headline')}
              />
              <Checkbox
                label="ROI Summary by Category"
                checked={selectedSections.financial.roi_summary}
                onChange={() => toggleSection('financial', 'roi_summary')}
                description="Quick Wins / Medium / Long-Term"
              />
              <Checkbox
                label="Detailed ROI Breakdown"
                checked={selectedSections.financial.roi_detailed}
                onChange={() => toggleSection('financial', 'roi_detailed')}
                description="Full table with time saved, costs, ROI %"
              />
              <Checkbox
                label="Cost Assumptions & Salary Benchmarks"
                checked={selectedSections.financial.assumptions}
                onChange={() => toggleSection('financial', 'assumptions')}
              />
            </SectionGroup>

            {/* Section Group C - Opportunities */}
            <SectionGroup
              title="Opportunities"
              isExpanded={expandedGroups.opportunities}
              onToggle={() => toggleGroup('opportunities')}
            >
              <Checkbox
                label="Opportunities Summary"
                checked={selectedSections.opportunities.summary}
                onChange={() => toggleSection('opportunities', 'summary')}
                description="Count by category"
              />
              <Checkbox
                label="Top Opportunities (Ranked)"
                checked={selectedSections.opportunities.top_n}
                onChange={() => toggleSection('opportunities', 'top_n')}
              />
              {selectedSections.opportunities.top_n && (
                <div className="ml-6 mt-2">
                  <label className="text-sm text-[#A0AEC0]">Top N count:</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={selectedSections.opportunities.top_n_count}
                    onChange={(e) => updateField('opportunities', 'top_n_count', parseInt(e.target.value) || 10)}
                    className="ml-2 w-20 px-2 py-1 rounded bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-sm"
                  />
                </div>
              )}
              <Checkbox
                label="Full Opportunity List"
                checked={selectedSections.opportunities.full_list}
                onChange={() => toggleSection('opportunities', 'full_list')}
              />
              <Checkbox
                label="Quick Wins Only"
                checked={selectedSections.opportunities.quick_wins}
                onChange={() => toggleSection('opportunities', 'quick_wins')}
              />
              <Checkbox
                label="Medium-Term Only"
                checked={selectedSections.opportunities.medium}
                onChange={() => toggleSection('opportunities', 'medium')}
              />
              <Checkbox
                label="Long-Term Only"
                checked={selectedSections.opportunities.long_term}
                onChange={() => toggleSection('opportunities', 'long_term')}
              />
              <Checkbox
                label="Filter by Status"
                checked={selectedSections.opportunities.filter_by_status}
                onChange={() => toggleSection('opportunities', 'filter_by_status')}
                description="Include only opportunities with specific status"
              />
              {selectedSections.opportunities.filter_by_status && (
                <div className="ml-6 mt-2 space-y-2">
                  <label className="text-sm text-[#A0AEC0] block mb-2">Select Status(es):</label>
                  {['Identified', 'Evaluating', 'Planning', 'In Progress', 'Implemented', 'Paused', 'Cancelled'].map(status => (
                    <label key={status} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSections.opportunities.selected_statuses.includes(status.toLowerCase().replace(' ', '_'))}
                        onChange={(e) => {
                          const statusValue = status.toLowerCase().replace(' ', '_');
                          const currentStatuses = selectedSections.opportunities.selected_statuses;
                          const newStatuses = e.target.checked
                            ? [...currentStatuses, statusValue]
                            : currentStatuses.filter(s => s !== statusValue);
                          updateField('opportunities', 'selected_statuses', newStatuses);
                        }}
                        className="w-4 h-4 rounded border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.05)] checked:bg-[#6366F1]"
                      />
                      <span>{status}</span>
                    </label>
                  ))}
                </div>
              )}
            </SectionGroup>

            {/* Section Group D - Roadmap */}
            <SectionGroup
              title="Roadmap"
              isExpanded={expandedGroups.roadmap}
              onToggle={() => toggleGroup('roadmap')}
            >
              <Checkbox
                label="Implementation Roadmap Overview"
                checked={selectedSections.roadmap.overview}
                onChange={() => toggleSection('roadmap', 'overview')}
                description="1-3 months / 3-6 / 6-12 month swimlanes"
              />
              <Checkbox
                label="Detailed Roadmap Tasks"
                checked={selectedSections.roadmap.detailed}
                onChange={() => toggleSection('roadmap', 'detailed')}
              />
              <Checkbox
                label="Recommended Next 3 Moves"
                checked={selectedSections.roadmap.next_moves}
                onChange={() => toggleSection('roadmap', 'next_moves')}
                description="90-day action plan"
              />
            </SectionGroup>

            {/* Section Group E - Org Chart & Role Analysis */}
            <SectionGroup
              title="Org Chart & Role Analysis"
              isExpanded={expandedGroups.org_chart}
              onToggle={() => toggleGroup('org_chart')}
            >
              {/* Org Chart Overlay - HIDDEN (Beta guardrail) */}
              {/* 
              <Checkbox
                label="Org Chart AI Overlay"
                checked={selectedSections.org_chart.overlay}
                onChange={() => toggleSection('org_chart', 'overlay')}
                description="Visual org chart with AI highlights"
              />
              */}
              <Checkbox
                label="Per-Role Opportunity Summary"
                checked={selectedSections.org_chart.role_table}
                onChange={() => toggleSection('org_chart', 'role_table')}
                description="Table: Role, Department, Opportunity, Priority"
              />
            </SectionGroup>

            {/* Section Group F - Agent Configs */}
            <SectionGroup
              title="Agent Configurations"
              isExpanded={expandedGroups.agent_configs}
              onToggle={() => toggleGroup('agent_configs')}
            >
              <Checkbox
                label="Include Agent Configuration Blueprints"
                checked={selectedSections.agent_configs.include}
                onChange={() => toggleSection('agent_configs', 'include')}
              />
              {selectedSections.agent_configs.include && (
                <Checkbox
                  label="Only For Selected Opportunities"
                  checked={selectedSections.agent_configs.selected_only}
                  onChange={() => toggleSection('agent_configs', 'selected_only')}
                  className="ml-6"
                />
              )}
            </SectionGroup>

            {/* Section Group G - Appendices */}
            <SectionGroup
              title="Appendices & Legal"
              isExpanded={expandedGroups.appendices}
              onToggle={() => toggleGroup('appendices')}
            >
              <Checkbox
                label="Methodology & Approach"
                checked={selectedSections.appendices.methodology}
                onChange={() => toggleSection('appendices', 'methodology')}
              />
              <div className="flex items-center gap-2 text-sm text-[#A0AEC0]">
                <CheckCircle size={16} className="text-[#10B981]" />
                <span>ROI & Savings Disclaimer (always included)</span>
              </div>
              <Checkbox
                label="Consultant Notes / Custom Text Block"
                checked={selectedSections.appendices.consultant_notes}
                onChange={() => toggleSection('appendices', 'consultant_notes')}
              />
              {selectedSections.appendices.consultant_notes && (
                <div className="ml-6 mt-2">
                  <textarea
                    value={selectedSections.appendices.custom_notes_text}
                    onChange={(e) => updateField('appendices', 'custom_notes_text', e.target.value)}
                    placeholder="Enter custom consultant notes..."
                    className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] min-h-[100px]"
                  />
                </div>
              )}
            </SectionGroup>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[rgba(255,255,255,0.1)] flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-colors"
          >
            Cancel
          </button>

          <button
            onClick={handleGenerate}
            disabled={!atLeastOneSelected || isGenerating}
            className="px-6 py-2 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <DownloadSimple size={18} />
                Export PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Save Preset Modal */}
      {showSavePreset && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-[#1A1B1E] rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-white mb-4">Save as New Preset</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#A0AEC0] mb-2">Preset Name *</label>
                <input
                  type="text"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  placeholder="e.g., Client Executive Pack"
                  className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A0AEC0] mb-2">Description (optional)</label>
                <textarea
                  value={newPresetDescription}
                  onChange={(e) => setNewPresetDescription(e.target.value)}
                  placeholder="Brief description of this preset..."
                  className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] min-h-[80px]"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowSavePreset(false)}
                className="flex-1 px-4 py-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePreset}
                className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white hover:opacity-90 transition-opacity"
              >
                Save Preset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionGroup = ({ title, isExpanded, onToggle, children }) => {
  return (
    <div className="glass rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[rgba(255,255,255,0.05)] transition-colors"
      >
        <span className="text-white font-semibold">{title}</span>
        {isExpanded ? (
          <CaretDown size={20} className="text-[#A0AEC0]" />
        ) : (
          <CaretRight size={20} className="text-[#A0AEC0]" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-4 py-3 space-y-3 border-t border-[rgba(255,255,255,0.1)]">
          {children}
        </div>
      )}
    </div>
  );
};

const Checkbox = ({ label, checked, onChange, description, className = "" }) => {
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 w-4 h-4 rounded border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.05)] checked:bg-[#6366F1] focus:ring-2 focus:ring-[#6366F1] cursor-pointer"
      />
      <div className="flex-1">
        <label className="text-sm text-white cursor-pointer" onClick={onChange}>
          {label}
        </label>
        {description && (
          <p className="text-xs text-[#718096] mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
};

export default ReportBuilderModal;
